import { Dispatch, MouseEventHandler, RefObject, SetStateAction, useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { v4 as uuid_v4 } from "uuid";

// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";

// MUI
import { DataGridPremiumProps, GridRowId, GridEventListener, useGridApiRef } from "@mui/x-data-grid-premium";
import { IconButton, Stack, Tooltip } from "@mui/material";

// Constants
import { FOOTER_PARAM_ID } from "@/Constants/tables";

// Types
import { CompensationDataRecord } from "./types";

// Icons
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import SaveIcon from "@Washouts/assets/icons/save-outline.svg";
import CancelIcon from "@Washouts/assets/icons/cancel.svg";
import RemoveIcon from "@Washouts/assets/icons/trash.svg";
import RightTriangleIcon from "@Washouts/assets/icons/right-triangle.svg";

const ADD_BUTTON_SIZE = 20;
const ACTION_BUTTON_SIZE = 18;

function AddRowButton({ addHandler }: Readonly<{ addHandler: MouseEventHandler }>) {
	return (
		<>
			<Stack component="div" className="text-primary absolute" sx={{ right: -5, top: ADD_BUTTON_SIZE / -2 }}>
				<AddCircleRoundedIcon
					className="cursor-pointer"
					onClick={addHandler}
					sx={{
						height: ADD_BUTTON_SIZE,
						width: ADD_BUTTON_SIZE,
					}}
				/>
				<Stack width={ADD_BUTTON_SIZE} height={ADD_BUTTON_SIZE} className="absolute left-0" top={ADD_BUTTON_SIZE}></Stack>
			</Stack>
			<Stack className="h-[2px] w-[104px] bg-gradient-to-r from-primary absolute left-0 top-[-1px]"></Stack>
		</>
	);
}

function RowActionButtons({
	saveHandler,
	cancelHandler,
	removeHandler,
}: Readonly<{
	saveHandler?: MouseEventHandler;
	cancelHandler?: MouseEventHandler;
	removeHandler?: MouseEventHandler;
}>) {
	return (
		<Stack
			component="div"
			direction="column"
			spacing={0}
			className={clsx("relative rounded-full bg-white px-0 py-[5px] drop-shadow-[1px_0px_3px_rgba(0,0,0,.25)]", {
				"top-[-37px]": removeHandler,
				"top-[-20px]": !removeHandler,
			})}
		>
			<Tooltip title="Cancel" arrow placement="left">
				<IconButton onClick={cancelHandler} aria-label="cancel" disableRipple>
					<img src={CancelIcon} alt="Cancel icon" width={ACTION_BUTTON_SIZE - 5} height={ACTION_BUTTON_SIZE - 5} />
				</IconButton>
			</Tooltip>
			<Tooltip title="Save (if no errors)" arrow placement="left">
				<IconButton onClick={saveHandler} aria-label="save" disableRipple>
					<img src={SaveIcon} alt="Save icon" width={ACTION_BUTTON_SIZE} height={ACTION_BUTTON_SIZE} />
				</IconButton>
			</Tooltip>
			{removeHandler && (
				<Tooltip title="Remove" arrow placement="left">
					<IconButton onClick={removeHandler} aria-label="remove" disableRipple>
						<img src={RemoveIcon} alt="Remove icon" width={ACTION_BUTTON_SIZE} height={ACTION_BUTTON_SIZE} />
					</IconButton>
				</Tooltip>
			)}
			<img className="absolute top-[calc(50%-11px)] left-[calc(100%-6px)]" src={RightTriangleIcon} alt="Right pointer icon" />
		</Stack>
	);
}

function isReadyToSave(apiRef: ReturnType<typeof useGridApiRef>) {
	const api = apiRef.current;
	if (!api?.state?.editRows) {
		return true;
	}
	let isDirty = true;
	let hasErrors = true;
	Object.keys(api.state.editRows).forEach((rowId) => {
		const row = api.state.editRows[rowId];
		hasErrors = !Object.keys(row).every((field) => !row[field].error);
		isDirty = !Object.keys(row).every((field) => !row[field].isDirty);
	});
	return !hasErrors && !isDirty;
}

export function CompensationRowAdder({ apiRef, containerRef, changeHandler, ...restProps }: Readonly<CompensationRowAdderProps>) {
	const [rows, setRows] = restProps.rowsState;
	const { editable = true } = restProps;
	const [isEditing, setIsEditing] = useState(false);
	const [editingRowId, setEditingRowId] = useState<GridRowId | undefined>(undefined);
	const [hoveredRowId, setHoveredRowId] = useState<GridRowId | undefined>(undefined);
	const [hoveredRowDomRect, setHoveredRowDomRect] = useState<DOMRect | undefined>(undefined);
	const [editRowDomRect, setEditRowDomRect] = useState<DOMRect | undefined>(undefined);
	const [addedRowId, setAddedRowId] = useState<GridRowId | undefined>(undefined);

	const computeTop = useCallback(
		(domRect?: DOMRect) => {
			const containerTop = containerRef?.current?.getBoundingClientRect().top ?? 0;
			const targetTop = domRect?.top ?? 0;
			return targetTop - containerTop;
		},
		[containerRef]
	);

	const computeLeft = useCallback(
		(domRect?: DOMRect) => {
			const containerLeft = containerRef?.current?.getBoundingClientRect().left ?? 0;
			const targetLeft = domRect?.left ?? 0;
			return targetLeft - containerLeft;
		},
		[containerRef]
	);

	const clearTmpState = useCallback(() => {
		setHoveredRowId(undefined);
		setHoveredRowDomRect(undefined);
	}, [setHoveredRowId, setHoveredRowDomRect]);

	const addHandler: MouseEventHandler<SVGSVGElement> = () => {
		if (!apiRef.current) {
			return;
		}
		if (isEditing) {
			return;
		}
		let index = -1;
		if (hoveredRowId === FOOTER_PARAM_ID) {
			index = rows.length;
		} else {
			index = rows.findIndex((element) => apiRef.current.getRowId(element) === hoveredRowId);
		}
		if (index === -1) {
			return;
		}
		const rowToAdd: CompensationDataRecord = { _id: uuid_v4(), rowIndex: index, columnsAddons: [] };
		let newRows = [];
		if (hoveredRowId === FOOTER_PARAM_ID) {
			newRows = [...rows, rowToAdd];
		} else {
			const rowsBefore = rows.slice(0, index);
			const rowsAfter = rows.slice(index);
			newRows = [...rowsBefore, rowToAdd, ...rowsAfter];
		}
		setAddedRowId(rowToAdd._id);
		setRows(newRows);
	};

	const mouseLeaveHandler: MouseEventHandler<HTMLDivElement> = () => {
		clearTmpState();
	};

	const saveHandler = () => {
		if (!apiRef.current) {
			return;
		}
		if (!isReadyToSave(apiRef)) {
			return;
		}
		const api = apiRef.current;
		const editingRowsIds = Object.keys(api.state.editRows);
		for (const rowId of editingRowsIds) {
			api.stopRowEditMode({ id: rowId });
		}
		setIsEditing(false);
		setEditRowDomRect(undefined);
		setAddedRowId(undefined);
		clearTmpState();
	};

	const cancelHandler = () => {
		if (!apiRef.current) {
			return;
		}
		const api = apiRef.current;
		const editingRowsIds = Object.keys(api.state.editRows);
		for (const rowId of editingRowsIds) {
			api.stopRowEditMode({ id: rowId, ignoreModifications: true });
		}
		if (addedRowId && editingRowId === addedRowId) {
			const addedRow = (api.getRow(addedRowId) as CompensationDataRecord) ?? {};
			setAddedRowId(undefined);
			const newRows = rows.filter((element) => element._id !== addedRow._id);
			setRows(newRows);
		}
		setEditRowDomRect(undefined);
		setEditingRowId(undefined);
		setIsEditing(false);
		setAddedRowId(undefined);
		clearTmpState();
	};

	const removeHandler = () => {
		if (!apiRef.current || !editingRowId) {
			return;
		}
		const api = apiRef.current;
		const editingRowsIds = Object.keys(api.state.editRows);
		for (const rowId of editingRowsIds) {
			api.stopRowEditMode({ id: rowId, ignoreModifications: true });
		}
		const editingRow = api.getRow(editingRowId);
		setEditingRowId(undefined);
		setEditRowDomRect(undefined);
		setIsEditing(false);
		clearTmpState();
		if (!isNaN(Number(editingRow.rowIndex))) {
			changeHandler?.(null, editingRow as CompensationDataRecord);
		}
	};

	const onRowMouseEnter: GridEventListener<"rowMouseEnter"> = (params) => {
		if (!apiRef.current) {
			return;
		}
		const elem = apiRef.current.getRowElement(params.id);
		const domRect = elem?.getBoundingClientRect();
		setHoveredRowId(params.id);
		setHoveredRowDomRect(domRect);
	};

	const onRowEditStart: GridEventListener<"rowEditStart"> = (params, _event) => {
		const editModeActive = Object.keys(apiRef.current.state.editRows).length > 0;
		if (!editModeActive) {
			const elem = apiRef.current.getRowElement(params.id);
			const domRect = elem?.getBoundingClientRect();
			setEditingRowId(params.id);
			setEditRowDomRect(domRect);
			setIsEditing(true);
		}
	};

	const onRowMouseLeave: GridEventListener<"rowMouseLeave"> = (params, event) => {
		const elem = apiRef.current.getRowElement(params.id);
		const domRect = elem?.getBoundingClientRect();
		const xInsideRect = event.clientX >= (domRect?.left ?? 0) && event.clientX <= (domRect?.right ?? 0);
		const yInsideRect = event.clientY >= (domRect?.top ?? 0) && event.clientY <= (domRect?.bottom ?? 0);
		const stillHovering = xInsideRect && yInsideRect;
		if (!stillHovering) {
			clearTmpState();
		}
	};

	const onRowCountChange: GridEventListener<"rowCountChange"> = () => {
		if (addedRowId) {
			try {
				let elem = apiRef.current.getRowElement(addedRowId);
				if (!elem) {
					// This is a corner case since the added row has no element yet
					// We need to get the footer element instead because it only happens when the added row is the last one
					elem = apiRef.current.getRowElement(FOOTER_PARAM_ID);
				}
				const domRect = elem?.getBoundingClientRect();
				setEditingRowId(addedRowId);
				setEditRowDomRect(domRect);
				apiRef.current.startRowEditMode({ id: addedRowId });
				setIsEditing(true);
			} catch (error) {
				errorLogger(getError(error));
			}
		}
	};

	useEffect(() => {
		const unsubscribeMouseEnter = apiRef.current?.subscribeEvent("rowMouseEnter", onRowMouseEnter);
		const unsubscribeMouseLeave = apiRef.current?.subscribeEvent("rowMouseLeave", onRowMouseLeave);
		const unsubscribeRowCount = apiRef.current?.subscribeEvent("rowCountChange", onRowCountChange);
		const unsubscribeRowEditStart = apiRef.current?.subscribeEvent("rowEditStart", onRowEditStart);

		return () => {
			unsubscribeMouseEnter?.();
			unsubscribeMouseLeave?.();
			unsubscribeRowCount?.();
			unsubscribeRowEditStart?.();
		};
	}, [apiRef, addedRowId]);

	const isAddPossible = editable && Boolean(rows?.length > 0) && Boolean(hoveredRowId) && !isEditing;

	return (
		<>
			{isAddPossible ? (
				<Stack
					className="absolute z-[1000]"
					sx={{
						top: computeTop(hoveredRowDomRect),
						left: computeLeft(hoveredRowDomRect),
					}}
					onMouseLeave={mouseLeaveHandler}
				>
					<AddRowButton addHandler={addHandler} />
				</Stack>
			) : null}
			{isEditing && editingRowId ? (
				<Stack
					id="row-action-buttons"
					className="absolute z-[1000]"
					sx={{
						top: editRowDomRect ? computeTop(editRowDomRect) : 0,
						left: -44,
					}}
				>
					<RowActionButtons
						saveHandler={saveHandler}
						cancelHandler={cancelHandler}
						removeHandler={
							!addedRowId && !isNaN(Number(apiRef.current.getRow(editingRowId).rowIndex)) ? removeHandler : undefined
						}
					/>
				</Stack>
			) : null}
		</>
	);
}

type CompensationRowAdderProps = {
	apiRef: ReturnType<typeof useGridApiRef>;
	containerRef: RefObject<HTMLDivElement>;
	editable?: boolean;
	rowsState: [CompensationDataRecord[], Dispatch<SetStateAction<CompensationDataRecord[]>>];
	changeHandler: DataGridPremiumProps["processRowUpdate"];
};
